//angular
import { Injectable, OnDestroy } from "@angular/core";

//third party
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { Subject } from "rxjs";

//local imports
import { NotificationDto } from "@core/data/http/dtos";
import { ApplicationConfiguration } from "@core/configuration";
import { NotificationFactory } from "@core/services/notification.factory";
import { AuthErrorConstants } from "@core/security/helpers";
import { AuthService } from "@core/security";
import { Messenger } from "@shared/data/messaging";
import { SignalRConnection } from "@shared/data/http/signalr";

import { NotificationReceivedMessage, UserLoggedInMessage, UserLoggedOutMessage } from "@core/messages";

/**
 * @summary A service that allows components to communicate with each other
 * @description This service uses signals to allow components to communicate with each other.
 */
@Injectable({
    providedIn: 'root'
})
export class CommunicationService implements OnDestroy {

    private readonly _destroying$ = new Subject<void>();

    private _signalrConnection?: SignalRConnection;
    private _hasConnected = false;
    private _initialized = false;

    constructor(
        private authService: AuthService,
        private messenger: Messenger,
        private applicationConfig: ApplicationConfiguration,
        private notificationFactory: NotificationFactory
    ) { }

    /**
     * @summary Connects to the signalr hub
     * @description This method nd subscribes to the token$ observable to get the access token
     * Connects to connects to the signalr hub and subscribes to notifications from the hub and relays them into the Messenger
     * @param url The url to connect to
     */
    public initialize(): Promise<void> {

        if (this._initialized) {
            console.warn(`CommunicationService: Already initialized, skipping re-initialization`);
            return Promise.resolve();
        }

        console.debug(`CommunicationService: Initializing`);
        
        this._initialized = true;
        this._signalrConnection = new SignalRConnection();

        const getToken = async () => {
            try {
                return await this.authService.acquireApplicationApiAccessToken();
            } catch (error) {
                console.error(`CommunicationService: Connection failed`, error);
                if (this.shouldStartInteractiveRelogin(error)) {
                    this.messenger.send(new UserLoggedOutMessage(true));
                }
                return "";
            }
        };

        this.messenger.subscribe(this, UserLoggedInMessage, () => {
            this.connectToBroadcast(getToken);
        });

        if (this.authService.hasActiveAccount()) {
            this.connectToBroadcast(getToken);
        }
        return Promise.resolve();
    }

    private connectToBroadcast(getToken: () => Promise<string>): void {
        if (this._hasConnected || !this._signalrConnection) {
            return;
        }

        this._hasConnected = true;
        this._signalrConnection.connect(
            `${this.applicationConfig.applicationUri}/${this.applicationConfig.tenant}/messaging/broadcast`,
            getToken,
            "BroadcastMessage",
            (data: NotificationDto) => {
                const notification = this.notificationFactory.createNotification(data);
                if (!notification) {
                    console.warn(`CommunicationService: Received notification with unknown type ${data.type}`);
                    return;
                }

                this.messenger.send(notification);
                this.messenger.send(new NotificationReceivedMessage(notification));
            }
        ).catch((error) => {
            this._hasConnected = false;
            console.error("CommunicationService: Failed to start SignalR connection", error);
            if (this.shouldStartInteractiveRelogin(error)) {
                this.messenger.send(new UserLoggedOutMessage(true));
            }
        });
    }

    private shouldStartInteractiveRelogin(error: unknown): boolean {
        if (error instanceof InteractionRequiredAuthError) {
            return true;
        }

        const errorCode = (error as { errorCode?: string } | undefined)?.errorCode?.toLowerCase();
        if (!errorCode) {
            return false;
        }

        return AuthErrorConstants.interactionRequiredCodes.has(errorCode);
    }

    async ngOnDestroy(): Promise<void> {
        this._destroying$.next(undefined);

        this.messenger.unsubscribeByOwner(this);

        await this._signalrConnection?.disconnect();

        this._destroying$.complete();
    }
}